import { useCallback, useState } from 'react'
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  SortChangedEvent,
} from 'ag-grid-community'
import _ from 'lodash'
import {
  RestoreType,
  useBulkSheetState as useBulkSheetStateBase,
} from '../../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'

type FilterModel = Record<string, unknown>

const RESTORE_TYPES: RestoreType[] = ['COLUMN_STATE', 'FILTER_STATE']

const toFilteredColumns = (
  gridApi: GridApi,
  filterModel: FilterModel
): ColDef[] =>
  Object.keys(filterModel)
    .map(colKey => gridApi.getColumnDef(colKey))
    .filter((col): col is ColDef => !!col)

const toSortedColumnState = (
  gridApi: GridApi,
  columnState: ColumnState[]
): SortedColumnState[] =>
  columnState
    .filter(col => !!col.sort)
    .map(col => {
      const def = gridApi.getColumnDef(col.colId)
      return {
        colId: col.colId,
        field: def?.field,
        headerName: def?.headerName,
        sort: col.sort,
      }
    })

export const useProgramManagementBulkSheetState = (
  gridApi: GridApi | null,
  functionUuid: string
) => {
  const base = useBulkSheetStateBase(
    functionUuid,
    functionUuid,
    UiStateKey.BulkSheetUIStateColumnAndFilter,
    RESTORE_TYPES
  )

  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )

  const restoreColumnState = useCallback(() => {
    if (!gridApi || _.isEmpty(base.columnState)) return
    gridApi.applyColumnState({
      state: base.columnState,
      applyOrder: true,
    })
  }, [base.columnState, gridApi])

  const syncDerivedStateFromRestored = useCallback(() => {
    if (!gridApi) return
    if (!_.isEmpty(base.filterState)) {
      setFilteredColumns(toFilteredColumns(gridApi, base.filterState ?? {}))
    }
    setSortColumnsState(toSortedColumnState(gridApi, base.columnState ?? []))
  }, [gridApi, base.columnState, base.filterState])

  const onFirstDataRendered = useCallback(
    (_e: FirstDataRenderedEvent) => {
      restoreColumnState()
      if (!_.isEmpty(base.filterState)) {
        gridApi?.setFilterModel(base.filterState ?? null)
      }
      syncDerivedStateFromRestored()
      base.finishRestoring()
    },
    [base, gridApi, restoreColumnState, syncDerivedStateFromRestored]
  )

  const rememberColumnState = useCallback(() => {
    const columnState = gridApi?.getColumnState()
    columnState && base.saveColumnState(columnState)
  }, [base, gridApi])

  const refreshSortColumnsState = useCallback(() => {
    if (!gridApi) return
    setSortColumnsState(toSortedColumnState(gridApi, gridApi.getColumnState()))
  }, [gridApi])

  const onSortChanged = useCallback(
    (_e: SortChangedEvent) => {
      rememberColumnState()
      refreshSortColumnsState()
    },
    [rememberColumnState, refreshSortColumnsState]
  )

  const onColumnMoved = useCallback(
    (_e: ColumnMovedEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => {
      if (!e.finished) return
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onColumnVisible = useCallback(
    (_e: ColumnVisibleEvent) => {
      rememberColumnState()
    },
    [rememberColumnState]
  )

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api.getFilterModel()
      base.saveFilterState(filterModel)
      setFilteredColumns(toFilteredColumns(e.api, filterModel))
    },
    [base]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!gridApi) return
      const colKey = column.colId || column.field || ''
      const { [colKey]: _omit, ...rest } = gridApi.getFilterModel()
      gridApi.setFilterModel(rest)
    },
    [gridApi]
  )

  const resetFilters = useCallback(() => {
    if (!gridApi) return
    gridApi.setFilterModel(null)
  }, [gridApi])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<any>) => {
      if (!gridApi) return
      const colIdStr = colId.toString()
      gridApi.applyColumnState({
        state: [{ colId: colIdStr, sort: null }],
      })
    },
    [gridApi]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    if (!gridApi) return
    gridApi.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridApi])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      if (!gridApi) return
      gridApi.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridApi]
  )

  const resetDerivedState = useCallback(() => {
    setFilteredColumns([])
    setSortColumnsState([])
  }, [])

  return {
    onFirstDataRendered,
    onSortChanged,
    onColumnMoved,
    onColumnResized,
    onColumnVisible,
    onFilterChanged,
    filteredColumns,
    sortColumnsState,
    onDeleteFilteredColumn,
    resetFilters,
    onDeleteSortedColumn,
    onDeleteSortedAllColumns,
    onChangeSortColumnState,
    columnState: base.columnState,
    filterState: base.filterState,
    saveColumnState: base.saveColumnState,
    saveFilterState: base.saveFilterState,
    resetDerivedState,
  }
}
